import React from 'react';
import useEditorStore, { LayoutEntity, PostEntity } from './editorStore';
import PostCardFrame from './PostCardFrame';
import LayoutSurface, { LayoutSurfaceKind } from './LayoutSurface';

/**
 * The canvas. Shows ONE surface at a time, each replicating its public
 * shell so the admin composes at the exact measure visitors will see:
 * - the posts feed (centered at the shared 820px content cap → 780px
 *   content box inside the card);
 * - a layout surface (custom page / fallback home / division callout).
 *
 * The public page's right rail (live games / registration) is deliberately
 * NOT represented — it's assembled from live data at request time, isn't
 * authorable, and the admin asked for it gone (M2 amendment).
 */

export interface ActiveLayoutSurface {
  layout: LayoutEntity;
  kind: LayoutSurfaceKind;
  contextLabel?: string;
}

interface Props {
  posts: PostEntity[];
  leagueName: string;
  layoutSurface: ActiveLayoutSurface | null;
  onSelectPost: (post: PostEntity) => void;
}

export default function CanvasStage({ posts, leagueName, layoutSurface, onSelectPost }: Props) {
  const surface = useEditorStore((s) => s.surface);
  const selected = useEditorStore((s) => s.selected);
  const selectedPostId = selected?.kind === 'post' ? selected.id : null;

  if (surface.kind === 'layout' && layoutSurface) {
    return (
      <main className="canvas-stage">
        <div className="posts-feed canvas-feed">
          <LayoutSurface
            layout={layoutSurface.layout}
            kind={layoutSurface.kind}
            leagueName={leagueName}
            contextLabel={layoutSurface.contextLabel}
          />
        </div>
      </main>
    );
  }

  return (
    <main className="canvas-stage">
      <div className="posts-feed canvas-feed">
        {posts.length === 0 && (
          <div className="canvas-feed__empty">
            <p>No posts yet.</p>
            <p>Click “+” in the sidebar to write your first post. It will appear here exactly as visitors will see it.</p>
          </div>
        )}
        {posts.map((post, i) => (
          <PostCardFrame
            key={post._id}
            post={post}
            featured={i === 0}
            selected={selectedPostId === post._id}
            leagueName={leagueName}
            onSelect={() => onSelectPost(post)}
          />
        ))}
      </div>
    </main>
  );
}
